import AddIcon from '@mui/icons-material/Add';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import {
  Button,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  Select,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { CartItemAdditionTypes } from '../../constants/event';
import useAgentInterception from '../../hooks/agentInterception/useAgentInterception.hooks';
import {
  cartActions,
  setSequenceId,
  setCartItemCollapseState,
} from '../../reducers/cartSlice';
import {
  dialogActions,
  selectItemAndResetNavigation,
  resetDialogSelectedItem,
} from '../../reducers/dialogSlice';
import { AgentInterceptionType } from '../../reducers/messagingSlice';
import { OrderStatus } from '../../reducers/orderSlice.constants';
import {
  CartItem,
  getAutoCombinedCartItemIdsCount,
  shouldCollapse,
} from '../../utils/cart';
import Colors from '../../utils/color';
import CartItemModGroups from './CartItemModGroups';
import { useIsCartItemSelected } from './CartScopeContext';
import OrderProgressBar from './OrderProgressBar';
import { upsell } from '../../utils/upsell';

import {
  addRefreshNonComboItem,
  cloneDeleteMUComboItem,
  refreshMUAutoCombinedItems,
  resetCombinedCartItemds,
} from '../../redux/features/autoCombo/autoCombo.slice';
import useAutoCombo from '../../hooks/autoCombo/useAutoCombo.hooks';
import { useAppSelector } from '../../app/hooks';
import { selectCartItemsQuantity } from '../../selectors/cart';
import useIssueReport from '../../hooks/issueReport/useIssueReport.hooks';
import { buildCartItemPointer } from '../../utils/issueReportItems';
import {
  ISSUE_FLAG_BADGE_CLASS,
  ISSUE_FLAG_BADGE_ICON_STYLE,
  ISSUE_FLAG_BADGE_REVEAL_SX,
  ISSUE_FLAG_BADGE_SX,
} from '../IssueReport/issueFlagBadgeStyle';
import { selectFeatureFlagHitl3 } from '../../redux/features/config/config.selector';
import { selectMenuModuleCartErrors } from '../../selectors/order';
import { selectCappedQuantityFlagsByCartItemId } from '../../redux/features/unresolvedItems/unresolvedItems.selector';
import {
  CAPPED_CART_ITEM_ATTR,
  formatCappedQuantityFlag,
} from '../../utils/unresolvedItems';
import {
  cartErrorMatchesItem,
  getModifierErrorPaths,
} from '../../utils/menuModuleError';
import { ItemCartActionButtons } from '../../constants/enums';
import useMenuUnificationOrderHandlers from '../../hooks/menuUnification/useMenuUnificationOrderHandler.hooks';
import {
  selectCanCopyToDraft,
  selectIsAgentActionBlocked,
} from '../../redux/features/agentMode/agentMode.selector';
import { BLOCKED_AFFORDANCE_STYLE } from '../../utils/agentModeAffordance';
import { isAutoCombinedLine } from '../../redux/features/draftCart/draftCart.thunks';
import CopyToDraftControl, { COPY_ANCHOR_STYLE } from './CopyToDraftControl';
interface ICartItemReview {
  cartItem: CartItem;
  quantity: number;
  status?: OrderStatus;
  collapseState: number;
}

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    marginTop: theme.spacing(2),
  },
  content: {
    padding: `${theme.spacing(1.5)} !important`,
    '& > div.cartitem-mod-group': {
      padding: '0 !important',
    },
  },
  mainItemname: {
    lineHeight: '1.57 !important',
    fontSize: `${theme.spacing(1.5)} !important`,
    fontWeight: 'bold !important',
  },
  lineItemsWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: '24px',
  },
  itemPrice: {
    paddingLeft: theme.spacing(2),
    fontSize: '0.75rem !important',
  },
  actionsWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    '& span': {
      cursor: 'pointer',
    },
  },
  itemActions: {
    display: 'flex',
    flexDirection: 'row-reverse',
    '& .MuiButtonBase-root.MuiButton-root': {
      textTransform: 'none',
      color: Colors.black,
      fontWeight: 'bold',
      flexDirection: 'row-reverse',
      textAlign: 'right',
      padding: '0px',
    },
  },
  selectQuantity: {
    '& .MuiSvgIcon-root': {
      display: 'none',
    },
    '&:before': {
      borderBottom: 'none !important',
    },
    '& .MuiSelect-select': {
      textAlign: 'center',
      minWidth: '40px !important',
      paddingRight: '2px !important',
      paddingLeft: '0px !important',
    },
  },
  autoCombinedText: {
    paddingBottom: theme.spacing(1),
  },
  // HITL-1621: "Customer asked for 30 — capped at 12" pill on a cart line whose
  // quantity the AI silently reduced — amber chip with a leading dot, per the
  // design doc, sitting below the modifiers.
  cappedQuantityFlag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    backgroundColor: '#FDF0E7',
    color: '#B33E00',
    border: '1px solid #F2C9AC',
    borderRadius: theme.spacing(0.625),
    fontSize: '11.5px',
    fontWeight: 500,
    padding: theme.spacing(0.375, 1),
    marginTop: theme.spacing(0.75),
    '&::before': {
      content: '""',
      width: 7,
      height: 7,
      borderRadius: '50%',
      backgroundColor: '#E05A00',
      flex: 'none',
    },
  },
  // ---- hitl_3_0 item-row refinements (gated; legacy is untouched) ----
  // Tighter card gap + content padding so more items fit without scrolling.
  cardHitl3: {
    marginTop: `${theme.spacing(1)} !important`,
  },
  contentHitl3: {
    padding: `${theme.spacing(1)} !important`,
    paddingBottom: `${theme.spacing(0.75)} !important`,
  },
  actionsWrapperHitl3: {
    alignItems: 'center',
    marginTop: theme.spacing(0.5),
  },
  // Quantity stepper as one segmented control: − | count | +.
  stepperHitl3: {
    display: 'inline-flex',
    alignItems: 'stretch',
    border: `1px solid ${Colors.alto}`,
    borderRadius: theme.spacing(0.75),
    overflow: 'hidden',
    '& .MuiIconButton-root': {
      borderRadius: 0,
      padding: theme.spacing(0.25, 0.5),
      color: Colors.daintree,
    },
    '& .MuiIconButton-root.Mui-disabled': {
      color: Colors.silverChalice,
    },
  },
  selectQuantityHitl3: {
    borderLeft: `1px solid ${Colors.alto}`,
    borderRight: `1px solid ${Colors.alto}`,
    // Center the quantity in its cell. The base selectQuantity uses text-align,
    // which a flex box ignores — so it needs justifyContent, plus symmetric
    // padding (the base sets asymmetric l/r padding) to sit dead center.
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '34px !important',
      padding: '0 4px !important',
    },
  },
  // Icon-only Duplicate / Remove (tooltip + aria-label supply the text). Remove
  // carries the destructive red.
  // HITL-1683: the `!important` colour below beats MUI's own `.Mui-disabled`
  // grey, so in `Shadow` these two read as fully live while refusing the click
  // — the cursor said not-allowed and the icon said press me. The disabled
  // colour has to carry `!important` for the same reason the base one does:
  // equal-or-higher specificity loses to an `!important` it does not match.
  //
  // `silverChalice` is what `DraftCartItemReview` already greys its own
  // Duplicate / Remove to, so the two columns agree about what "refused" looks
  // like.
  //
  // Nested selectors, not new named rules: JSS numbers class names off the
  // KEYS of this object, so `&.Mui-disabled` adds no name and renumbers nothing.
  iconActionHitl3: {
    padding: `${theme.spacing(0.5)} !important`,
    color: `${Colors.daintree} !important`,
    '&.Mui-disabled': {
      color: `${Colors.silverChalice} !important`,
    },
  },
  iconActionDangerHitl3: {
    padding: `${theme.spacing(0.5)} !important`,
    color: `${Colors.guardsmanRed} !important`,
    '&.Mui-disabled': {
      color: `${Colors.silverChalice} !important`,
    },
  },
}));

export const CartItemReview = (props: ICartItemReview) => {
  const { cartItem, quantity, status, collapseState } = props;
  const cartItemRef = useRef<HTMLDivElement>(null);
  const actionableElementsRef = useRef<any[]>([]);
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    isUnifiedMenu,
    isPRPAutoComboEnabled,
    isBMAutoComboEnabled,
    autoCombinedCartItemsQuantity,
  } = useAutoCombo();
  const { sequenceId, cartItems } = useSelector(
    (state: RootState) => state.cart
  );
  const { selectedItemCartId } = useSelector(
    (state: RootState) => state.dialog
  );
  /**
   * HITL-1683 — scope-aware, because `selectedItemCartId` is ONE number over TWO
   * id spaces now and both slices number `cartItemId` from 1. Selecting My Cart
   * line 3 was putting the selection border on Live Order line 3 as well, and
   * scrolling the column to it.
   *
   * HITL-1617 added `useIsCartItemSelected` for exactly this and scoped the two
   * mod-group components. `CartScopeContext`'s own comment says this file
   * "becomes reachable from the draft in HITL-1617, and that is where [it] gets
   * scoped" — it did not, and this closes that. All THREE sites in this file
   * take it: the border, the scroll, and the delete handler's reselection.
   *
   * The centre pane's two comparisons (`CartItemDetail`, `RecursiveModGroup`)
   * are deliberately left bare: that pane renders only the ONE item the dialog
   * has selected, resolved out of whichever slice owns it, so there is no second
   * scope there for an id to collide with.
   */
  const isSelected = useIsCartItemSelected(
    selectedItemCartId,
    cartItem.cartItemId
  );

  const { sendAgentInterception } = useAgentInterception();
  const quantityList: number[] = new Array(19).fill(0);

  const isPRPAutoCombined = !!(
    isPRPAutoComboEnabled && cartItem.autoCombinedCartItemIds?.length
  );
  const isMUAutoCombined = !!(
    isUnifiedMenu &&
    isBMAutoComboEnabled &&
    cartItem.isAutoCombined
  );
  const isCartItemAutoCombined = isPRPAutoCombined || isMUAutoCombined;
  const cartItemsQuantity = useAppSelector(selectCartItemsQuantity);
  // HITL-1606: outside Intercept the agent may look but not act. UX only —
  // useAgentInterception refuses the emit regardless.
  const isAgentActionBlocked = useAppSelector(selectIsAgentActionBlocked);
  // HITL-1614: `Shadow` only. In `Intercept` these lines already ARE the order,
  // so there is nothing to copy into a draft. An auto-combined line is excluded
  // because it is a combo assembled from other cart lines — its
  // `autoCombinedCartItemIds` address `cartSlice`'s id space and mean nothing in
  // the draft. Hypothesis lines never reach here at all; `OrderReview` renders
  // those through `HypothesisItemReview`.
  const canCopyToDraft = useAppSelector(selectCanCopyToDraft);
  // hitl_3_0 only: refined item row (segmented stepper + icon-only actions +
  // tighter padding). Legacy keeps the text-button layout untouched.
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const menuModuleCartErrors = useAppSelector(selectMenuModuleCartErrors);
  // The 7.0 gate gates the ICON. The thunk already refuses to file with the feature
  // off, so this is not a second guard on the network call — it stops a dead control
  // appearing on every cart row in a store that has no issue reporting.
  const { flagIssue, isHitl7IssueReportingEnabled: isIssueReportingEnabled } =
    useIssueReport();

  /**
   * HITL-1655 — file a report about THIS row.
   *
   * `stopPropagation` because the card's own `onClick` selects the item and drives
   * ItemBuilder navigation. Flagging is a statement about the row, not a request to go
   * and edit it, and yanking the agent's work surface out from under them as a side
   * effect of reporting would be its own bug.
   *
   * The item pointer travels on the FILE, not on a later commit: the report has to name
   * its item even if the agent never classifies it, which is the whole advantage a
   * gesture has over the Issue button.
   */
  const flagCartItem = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    flagIssue({
      source: 'cart_item',
      // NO seeded issue_type on purpose. "What is wrong with this item" is genuinely
      // open — missing, incorrect, a modifier problem, a price problem — so seeding one
      // would be a guess, and unlike the item it is a guess about the agent's meaning.
      // The report lands unclassified and the panel asks.
      issue_item: buildCartItemPointer(cartItem),
    });
  };
  // HITL-1621: the AI capped this line's quantity. Keyed by cartItemId (the
  // selector resolves the producer's item name to exactly one line, or none);
  // empty map when the unresolved-items flag is off.
  const cappedQuantityFlags = useAppSelector(
    selectCappedQuantityFlagsByCartItemId
  );
  const cappedQuantity = cappedQuantityFlags[String(cartItem.cartItemId)];

  const handleCollapseStateChange = useCallback(() => {
    dispatch(setCartItemCollapseState({ cartItemId: cartItem.cartItemId }));
  }, [dispatch]);

  const sendAgentInterventionEvent = (description: string) => {
    sendAgentInterception({
      data: {
        type: AgentInterceptionType.ORDER_CHANGED,
        description,
      },
    });
  };

  const {
    menuUnificationOrderFailureCallback,
    menuUnificationOrderSuccessCallback,
  } = useMenuUnificationOrderHandlers();

  const cloneDeleteAutoCombinedCartItems = (operator: string) => {
    if (isMUAutoCombined) {
      dispatch(
        cloneDeleteMUComboItem({
          cartItemId: cartItem.cartItemId.toString(),
          operator,
          sequenceId,
        })
      );
      if (operator === '+') {
        dispatch(setSequenceId(sequenceId + 1));
      } else if (
        operator === '-' &&
        Object.keys(autoCombinedCartItemsQuantity).length === 1
      ) {
        // Only 1 item and it is being deleted -- so clear the entire cart
        dispatch(resetDialogSelectedItem());
        dispatch(cartActions.clearCart());
        dispatch(resetCombinedCartItemds());
      }
      dispatch(
        refreshMUAutoCombinedItems({
          menuUnificationOrderFailureCallback,
          menuUnificationOrderSuccessCallback,
        })
      );
    }
  };

  const cloneCartItem = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isMUAutoCombined) {
      cloneDeleteAutoCombinedCartItems('+');
    } else if (isPRPAutoComboEnabled && cartItem.autoCombinedCartItemIds) {
      const autoCombinedCartItemIdsCount = getAutoCombinedCartItemIdsCount(
        cartItem.autoCombinedCartItemIds
      );
      Object.keys(autoCombinedCartItemIdsCount).forEach((cartItemId) =>
        dispatch(
          cartActions.updateQuantity({
            cartItemId,
            quantity:
              cartItemsQuantity[cartItemId] +
              autoCombinedCartItemIdsCount[cartItemId],
          })
        )
      );
    } else {
      const cartItemIdString = cartItem.cartItemId.toString();

      // The quantity shown to user is used for cloning the item --- as for brand-menu non-combined item the quantity may not match
      dispatch(
        cartActions.cloneCartItem({
          id: cartItemIdString,
          quantity,
        })
      );
      dispatch(dialogActions.cloneSelectedItem({ cartItemId: sequenceId + 1 }));
    }

    sendAgentInterventionEvent(`Clone ${cartItem.name} in the same order`);
    if (isUnifiedMenu) {
      // Check upsell and auto reject it if the cart context changes
      upsell.settle();
    }
  };

  const deleteCartItem = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isMUAutoCombined) {
      cloneDeleteAutoCombinedCartItems('-');
    } else if (isPRPAutoComboEnabled && cartItem.autoCombinedCartItemIds) {
      const autoCombinedCartItemIdsCount = getAutoCombinedCartItemIdsCount(
        cartItem.autoCombinedCartItemIds
      );
      Object.keys(autoCombinedCartItemIdsCount).forEach((cartItemId) =>
        dispatch(
          cartActions.updateQuantity({
            cartItemId,
            quantity:
              cartItemsQuantity[cartItemId] -
              autoCombinedCartItemIdsCount[cartItemId],
          })
        )
      );
    } else {
      dispatch(cartActions.deleteCartItem(cartItem.cartItemId.toString()));
      if (isSelected) {
        const cartItemIds = Object.keys(cartItems)
          .filter((id) => id !== selectedItemCartId.toString())
          .map((id) => parseInt(id));
        const newCartItemId = Math.max(...cartItemIds);
        if (newCartItemId > 0) {
          dispatch(
            dialogActions.selectItemAndResetNavigation({
              item: cartItems[newCartItemId.toString()],
              itemCartId: newCartItemId,
            })
          );
        } else {
          dispatch(dialogActions.selectItemAndResetNavigation(undefined));
        }
      }
    }
    if (isUnifiedMenu) {
      // Check upsell and auto reject it if the cart context changes
      upsell.settle();
    }
    sendAgentInterventionEvent(`${cartItem.name} is removed from order`);
  };

  const updateQuantity = (newQuantity: number) => {
    if (isCartItemAutoCombined) return; // not support for auto combined item

    const cartItemIdString = cartItem.cartItemId.toString();
    if (Object.keys(autoCombinedCartItemsQuantity).includes(cartItemIdString)) {
      // Update the quantity in non-combined autoComboItemQuantity
      dispatch(
        addRefreshNonComboItem({
          cartItemId: cartItemIdString,
          cartItem,
          quantity: newQuantity,
        })
      );
    }

    dispatch(
      cartActions.updateQuantity({
        cartItemId: cartItemIdString,
        quantity: newQuantity,
      })
    );
    sendAgentInterventionEvent(
      `Quantity of ${cartItem.name} changed to ${newQuantity}`
    );
  };

  const handleDecreaseQuantity = () => {
    if (quantity > 1) {
      updateQuantity(quantity - 1);
    }
  };

  const handleIncreaseQuantity = () => {
    if (quantity < 19) {
      updateQuantity(quantity + 1);
    }
  };

  const onCardClick = (e: React.MouseEvent) => {
    // HITL-1613 (follow-up): in Shadow the cart is a display, not a control.
    //
    // This reverses what this ticket first shipped, which let the click through
    // on the reasoning that selecting a line to READ it is the point of Shadow.
    // In the running console it is not: `selectItemAndResetNavigation` opens the
    // item in the centre pane, and every modifier in that pane is refused by
    // HITL-1612 — so the click traded one dead control for a pane full of them.
    // The collapse toggle goes with it rather than being carved out, because a
    // card that reacts to some clicks and not others is harder to read than one
    // that reacts to none.
    //
    // Guarded here rather than at the three dispatches below so there is one
    // answer for the whole card, and so the `actionableElementsRef` check keeps
    // working: the stepper, Remove and Duplicate are already refused in their
    // own handlers and must not fall through to this one either.
    if (isAgentActionBlocked) return;

    const target = e.target as HTMLElement;

    const isSelectDropdownClick =
      target.closest('.MuiMenuItem-root') ||
      target.closest('.MuiMenu-root') ||
      target.closest('.MuiPaper-root[role="presentation"]');

    const isActionableClick =
      actionableElementsRef.current.some(
        (element) => element && element.contains(target)
      ) || isSelectDropdownClick;

    if (!isActionableClick) {
      handleCollapseStateChange();
    }

    if (isCartItemAutoCombined) return;
    dispatch(
      dialogActions.selectItemAndResetNavigation({
        item: cartItem,
        itemCartId: cartItem.cartItemId,
      })
    );
    if (isUnifiedMenu) {
      // Check upsell and auto reject it if the cart context changes
      upsell.settle();
    }
  };
  const collapse = shouldCollapse(collapseState, status);
  // A menu-module error whose path is just this item (no modifier segments)
  // means the item itself is the failing node — highlight its name.
  const isItemLevelError = menuModuleCartErrors.some(
    (error) =>
      cartErrorMatchesItem(error, cartItem.cartItemId, cartItem.name) &&
      error.nodePath.length === 1
  );
  const itemColor = isItemLevelError ? Colors.Thunderbird : Colors.daintree;

  const itemCardBorder = isSelected
    ? `3px solid ${itemColor}`
      : // hitl_3_0: unselected cards sit on a light background where the bare
      // elevation shadow leaves the side edges invisible, so give them a subtle
      // border. Legacy keeps the borderless (shadow-only) look.
      isHitl3
      ? `1px solid ${Colors.alto}`
      : 'unset';

  useEffect(() => {
    if (isSelected) {
      cartItemRef.current?.scrollIntoView();
    }
    // Keyed on the scoped value, not on the raw id: a selection landing in the
    // OTHER column must not scroll this one.
  }, [isSelected]);

  const card = (
    <Card
      id="cart-item"
      className={
        isHitl3 ? `${classes.card} ${classes.cardHitl3}` : classes.card
      }
      elevation={3}
      sx={{
        borderRadius: 1.5,
        // HITL-1613 (follow-up): the pointer promised the click that the guard in
        // `onCardClick` now refuses, so in Shadow the card takes the same
        // not-allowed cursor as its action row. `default` for a combined card
        // stays ahead of it — that one is inert for a different reason and has
        // never claimed otherwise.
        cursor: isCartItemAutoCombined
          ? 'default'
          : isAgentActionBlocked
          ? 'not-allowed'
          : 'pointer',
        backgroundColor: `${
          cartItem.addedBy === CartItemAdditionTypes.AI
            ? Colors.aiItemIndicator
            : isCartItemAutoCombined
            ? Colors.gallery
            : Colors.white
        }`,
        // HITL-1655, and ONLY when the badge is actually rendered. `Card` is a `Paper`
        // with `overflow: hidden`, which would clip the third of the badge that hangs
        // outside the corner; `position: static` would anchor it to whichever ancestor
        // is positioned instead of to this card. Both are applied conditionally
        // because both are real changes to a card that has none of them today, and a
        // console without 7.0 must render byte for byte what 6.x rendered.
        ...(isIssueReportingEnabled
          ? {
              position: 'relative',
              overflow: 'visible',
              ...ISSUE_FLAG_BADGE_REVEAL_SX,
            }
          : {}),
      }}
      onClick={onCardClick}
      ref={cartItemRef}
      // Scroll target for the panel's quantity card — only present while
      // flagged, so the flag-off DOM is unchanged.
      {...(cappedQuantity
        ? { [CAPPED_CART_ITEM_ATTR]: String(cartItem.cartItemId) }
        : {})}
      style={{
        // HITL-1621: amber stripe marks a capped line; it wins over the
        // selection border on the left edge only, so selection stays readable.
        borderLeft: cappedQuantity
          ? `4px solid ${Colors.internationalOrange}`
          : itemCardBorder,
        borderRight: itemCardBorder,
        borderTop: itemCardBorder,
      }}
    >
      {isIssueReportingEnabled && (
        // A SIBLING of the content, anchored to the card's own corner — so it says
        // "about this item" without taking a position among the controls that act on
        // it. Deliberately the opposite corner from Remove and Duplicate: the costs
        // are asymmetric, since a stray flag is discardable inside the undo window
        // and a stray Remove mid-order is not recoverable at all.
        <IconButton
          className={ISSUE_FLAG_BADGE_CLASS}
          sx={ISSUE_FLAG_BADGE_SX}
          title="Report an issue with this item"
          aria-label={`Report an issue with ${cartItem.name}`}
          data-testid="cart-item-flag"
          onClick={flagCartItem}
        >
          <FlagOutlinedIcon style={ISSUE_FLAG_BADGE_ICON_STYLE} />
        </IconButton>
      )}
      <CardContent
        className={
          isHitl3
            ? `${classes.content} ${classes.contentHitl3}`
            : classes.content
        }
      >
        <div className={classes.lineItemsWrapper}>
          <Typography
            variant="subtitle2"
            className={classes.mainItemname}
            style={{ color: itemColor }}
          >
            {cartItem.name}
          </Typography>
          {collapse ? (
            <ExpandMoreIcon style={{ padding: 0 }}></ExpandMoreIcon>
          ) : (
            <ExpandLessIcon style={{ padding: 0 }}></ExpandLessIcon>
          )}
        </div>
        {collapse ? null : (
          <>
            {isCartItemAutoCombined && (
              <Typography
                variant="subtitle2"
                className={`${classes.mainItemname} ${classes.autoCombinedText}`}
                style={{ color: itemColor }}
              >
                (Auto-combined)
              </Typography>
            )}
            {cartItem && cartItem.childModifierGroups && (
              <CartItemModGroups
                cartItem={cartItem}
                modGroupCartItem={cartItem}
                quantity={quantity}
                selectable={!isCartItemAutoCombined}
                errorNodePaths={getModifierErrorPaths(
                  menuModuleCartErrors,
                  cartItem.cartItemId,
                  cartItem.name
                )}
              />
            )}
          </>
        )}
        {/* HITL-1621: below the modifiers per the design doc, and outside the
            collapse block so a silently reduced quantity stays visible on a
            collapsed card too. */}
        {cappedQuantity && (
          <div className={classes.cappedQuantityFlag}>
            {formatCappedQuantityFlag(cappedQuantity)}
          </div>
        )}

        {/* HITL-1613: this row owns its own cursor while the mode is silent.
            MUI's disabled state sets `pointer-events: none`, so without it the
            cursor fell through to the CARD's cursor and the greyed-out stepper
            / Remove / Duplicate still invited the press.
            The card now carries `not-allowed` too (see the `sx` above), so the
            two agree — but this stays explicit rather than leaning on
            inheritance, because the fall-through is what made the bug invisible
            the first time. Inline rather than a `makeStyles` rule so no class
            name in this sheet renumbers. */}
        <div
          className={
            isHitl3
              ? `${classes.actionsWrapper} ${classes.actionsWrapperHitl3}`
              : classes.actionsWrapper
          }
          style={isAgentActionBlocked ? BLOCKED_AFFORDANCE_STYLE : undefined}
        >
          <div className={isHitl3 ? classes.stepperHitl3 : undefined}>
            <IconButton
              ref={(el) =>
                (actionableElementsRef.current[
                  ItemCartActionButtons.DECREASE_QUANTITY
                ] = el)
              }
              size="small"
              onClick={handleDecreaseQuantity}
              disabled={isCartItemAutoCombined || isAgentActionBlocked}
            >
              <RemoveIcon fontSize={isHitl3 ? 'small' : undefined} />
            </IconButton>
            <Select
              value={quantity}
              size="small"
              variant="standard"
              onChange={(e) => updateQuantity(e.target.value as number)}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 200 },
                },
              }}
              className={
                isHitl3
                  ? `${classes.selectQuantity} ${classes.selectQuantityHitl3}`
                  : classes.selectQuantity
              }
              disabled={isCartItemAutoCombined || isAgentActionBlocked}
            >
              {quantityList.map((q, index) => (
                <MenuItem
                  key={`quantity-${index}`}
                  value={index + 1}
                  id={`quantity-${index}`}
                >
                  {index + 1}
                </MenuItem>
              ))}
            </Select>
            <IconButton
              ref={(el) =>
                (actionableElementsRef.current[
                  ItemCartActionButtons.INCREASE_QUANTITY
                ] = el)
              }
              size="small"
              onClick={handleIncreaseQuantity}
              disabled={isCartItemAutoCombined || isAgentActionBlocked}
            >
              <AddIcon fontSize={isHitl3 ? 'small' : undefined} />
            </IconButton>
          </div>
          <div className={classes.itemActions}>
            {isHitl3 ? (
              // Icon-only under hitl_3_0 (tooltip + aria-label carry the label);
              // DOM order Remove→Duplicate + row-reverse renders Duplicate | Remove.
              <>
                <IconButton
                  ref={(el) =>
                    (actionableElementsRef.current[
                      ItemCartActionButtons.REMOVE
                    ] = el)
                  }
                  className={classes.iconActionDangerHitl3}
                  size="small"
                  title="Remove"
                  aria-label="Remove"
                  disabled={isAgentActionBlocked}
                  onClick={deleteCartItem}
                >
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  ref={(el) =>
                    (actionableElementsRef.current[
                      ItemCartActionButtons.DUPLICATE
                    ] = el)
                  }
                  className={classes.iconActionHitl3}
                  size="small"
                  title="Duplicate"
                  aria-label="Duplicate"
                  disabled={isAgentActionBlocked}
                  onClick={cloneCartItem}
                >
                  <ContentCopyOutlinedIcon fontSize="small" />
                </IconButton>
              </>
            ) : (
              <>
                <Button
                  ref={(el) =>
                    (actionableElementsRef.current[
                      ItemCartActionButtons.REMOVE
                    ] = el)
                  }
                  variant="text"
                  onClick={deleteCartItem}
                  size="small"
                  disabled={isAgentActionBlocked}
                >
                  Remove
                </Button>
                <Button
                  ref={(el) =>
                    (actionableElementsRef.current[
                      ItemCartActionButtons.DUPLICATE
                    ] = el)
                  }
                  variant="text"
                  onClick={cloneCartItem}
                  size="small"
                  disabled={isAgentActionBlocked}
                >
                  Duplicate
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
      <OrderProgressBar status={status} cartItem={cartItem} />
    </Card>
  );

  // HITL-1614: the copy control needs a positioned ancestor to sit ON the card's
  // left border. Rather than adding `position: relative` to the Card's own `sx`
  // — which would change its emotion hash and churn every existing snapshot —
  // the anchor is an extra wrapper that exists ONLY when the control does. With
  // the flag off, or in any mode but `Shadow`, the tree below is exactly what it
  // has always been.
  // HITL-1644: the auto-combo exclusion moved to `isAutoCombinedLine` so this
  // gate and the copy thunks cannot drift — the cart-level copy has to skip
  // exactly the lines this control is not offered on. The predicate itself is
  // unchanged; its reasoning now lives beside it in `draftCart.thunks`.
  if (!canCopyToDraft || isAutoCombinedLine(cartItem)) return card;

  return (
    <div style={COPY_ANCHOR_STYLE}>
      <CopyToDraftControl cartItem={cartItem} />
      {card}
    </div>
  );
};
